import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PARTNER_TYPES } from "../../data/site";
import { Field, GdprConsent, FormSuccess, inputCls } from "./FormBits";

const initial = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  partner_type: PARTNER_TYPES[0],
  country: "",
  specialisms: "",
  message: "",
  gdpr_consent: false,
};

export const PartnerForm = ({ defaultType }) => {
  const [form, setForm] = useState({ ...initial, partner_type: defaultType || PARTNER_TYPES[0] });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.contact_name.trim().length < 2) e.contact_name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (form.phone.trim().length < 5) e.phone = "Please enter a contact number.";
    if (!form.gdpr_consent) e.gdpr_consent = "GDPR consent is required to proceed.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/partners/register", form);
      setDone(true);
      toast.success("Partner registration submitted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <FormSuccess
        title="Thank you for your interest in partnering with us"
        message="Our partnerships team will review your details and respond soon to explore how we can collaborate."
        onReset={() => { setForm(initial); setDone(false); }}
        resetLabel="Register another partner"
      />
    );

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="partner-form" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Company / agency name" error={errors.company_name}>
          <input className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="partner-company" />
        </Field>
        <Field label="Contact name" required error={errors.contact_name}>
          <input className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="partner-contact" />
        </Field>
        <Field label="Email" required error={errors.email}>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="partner-email" />
        </Field>
        <Field label="Phone" required error={errors.phone}>
          <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="partner-phone" />
        </Field>
        <Field label="Partnership type" required>
          <select className={inputCls} value={form.partner_type} onChange={(e) => set("partner_type", e.target.value)} data-testid="partner-type">
            {PARTNER_TYPES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Country / region">
          <input className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="partner-country" />
        </Field>
      </div>
      <Field label="Specialisms / areas of focus">
        <textarea rows={3} className={inputCls} value={form.specialisms} onChange={(e) => set("specialisms", e.target.value)} placeholder="e.g. Nursing, Allied Health, international recruitment..." data-testid="partner-specialisms" />
      </Field>
      <Field label="Tell us about your partnership goals">
        <textarea rows={3} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="partner-message" />
      </Field>
      <GdprConsent checked={form.gdpr_consent} onChange={(v) => set("gdpr_consent", v)} error={errors.gdpr_consent} />
      <button type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-light disabled:opacity-70 sm:w-auto" data-testid="partner-submit">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Become a Recruitment Partner
      </button>
    </form>
  );
};

export default PartnerForm;
